import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI, clearSession } from '../services/api';

interface Role {
  id: number;
  name: string;
  displayName: string;
  color: string;
}

interface User {
  id: string;
  username: string;
  avatar?: string;
  rank: string;
  coins: number;
  playtime: number;
  joinedAt: string;
  lastLogin: string;
  minecraftUUID?: string;
  roles?: Role[];
  billingPreference?: 'receipt' | 'invoice';
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: string[];
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  isAdmin: boolean;
  login: (username: string, password: string, twoFactorCode?: string, rememberMe?: boolean) => Promise<{ requiresTwoFactor?: boolean }>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  refreshUser: () => Promise<void>;
  refreshPermissions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [permissions, setPermissions] = useState<string[]>([]);

  const fetchPermissions = async () => {
    try {
      const response = await authAPI.getMyPermissions();
      setPermissions(response.data.permissions || []);
    } catch {
      setPermissions([]);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (token) {
        try {
          const response = await authAPI.getProfile();
          setUser(response.data);
          await fetchPermissions();
        } catch (error) {
          // Token refresh failed or no valid session — clear only if no tokens remain
          // (the interceptor already cleared them if refresh failed)
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const hasPermission = (permission: string): boolean => {
    if (permissions.includes('admin.full')) return true;
    return permissions.includes(permission);
  };

  const hasAnyPermission = (perms: string[]): boolean => {
    if (permissions.includes('admin.full')) return true;
    return perms.some(p => permissions.includes(p));
  };

  const hasAllPermissions = (perms: string[]): boolean => {
    if (permissions.includes('admin.full')) return true;
    return perms.every(p => permissions.includes(p));
  };

  const isAdmin = hasAnyPermission(['admin.access', 'admin.full']);

  const login = async (username: string, password: string, twoFactorCode?: string, rememberMe?: boolean): Promise<{ requiresTwoFactor?: boolean }> => {
    const response = await authAPI.login(username, password, twoFactorCode);

    if (response.data.requiresTwoFactor) {
      return { requiresTwoFactor: true };
    }

    clearSession();

    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem('token', response.data.accessToken);
    storage.setItem('refreshToken', response.data.refreshToken);
    if (rememberMe) {
      localStorage.setItem('rememberMe', 'true');
    }

    setUser(response.data.user);
    await fetchPermissions();
    return {};
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch {
      // Ignore errors — we clear local state regardless
    }
    clearSession();
    setUser(null);
    setPermissions([]);
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getProfile();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const refreshPermissions = async () => {
    await fetchPermissions();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        permissions,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        isAdmin,
        login,
        logout,
        updateUser,
        refreshUser,
        refreshPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
